import {
  StoreImageEnum,
  defaultDesignValues,
  useDesignContext,
} from '@systemeio/digital-store-shared'
import { LanguageCodeEnum } from '@systemeio/ui'
import { useCourse as useCoursePrecache } from 'modules/courses/courses/hooks/use-course'
import { defaultDigitalProductErrors } from 'modules/digital-product/constants/default-values'
import { DigitalResourcesTypeEnum } from 'modules/digital-product/enums/digital-resources-type-enum'
import { useDigitalProduct } from 'modules/digital-product/hooks/use-digital-product'
import {
  DigitalProductChangeInterface,
  DigitalProductErrorInterface,
} from 'modules/digital-product/types/digital-product-interface'
import ItemActions from 'modules/stores/store/components/item-actions'
import ItemTabs, { ItemTabType } from 'modules/stores/store/components/item-tabs'
import ThankYouTab from 'modules/stores/store/components/tabs/thank-you-tab'
import { STORE_ITEM_CLASS_NAME } from 'modules/stores/store/constants/store-item-class-name'
import Thumbnail from 'modules/stores/store/content/product/components/thumbnail'
import Checkout from 'modules/stores/store/content/product/digital-product/components/checkout'
import { ProductDownloadTabEnum } from 'modules/stores/store/content/product/digital-product/product-download/enum/product-download-tab-enum'
import { defaultErrors } from 'modules/stores/store/content/product/digital-product/product-e-course/constants/default-errors'
import { defaultValues } from 'modules/stores/store/content/product/digital-product/product-e-course/constants/default-values'
import {
  ECourseTabEnum,
  tabLocoKeys,
} from 'modules/stores/store/content/product/digital-product/product-e-course/enum/e-course-tab-enum'
import { useErrorMessageInTabs } from 'modules/stores/store/content/product/digital-product/product-e-course/hooks/use-error-message-in-tabs'
import { getNextTab } from 'modules/stores/store/content/product/digital-product/product-e-course/utils/get-next-tab-e-course'
import {
  ProductECourseItemEditType,
  ProductECourseItemErrorsType,
  ProductECourseItemViewType,
} from 'modules/stores/store/content/product/types/product-interface'
import { useStoreEditItemContext } from 'modules/stores/store/context/store-item-edit-context'
import { useStore } from 'modules/stores/store/hooks/use-store'
import { useStoreContactFields } from 'modules/stores/store/hooks/use-store-contact-fields'
import { StoreId } from 'modules/stores/types/stores-interface'
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import { ConfirmationModalBeforeExit } from 'shared/confirmation-modal-before-exit/component'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { deepCompare } from 'shared/utils/deepCompare'
import { twMerge } from 'tailwind-merge'
import { useSaveItemProduct } from '../hooks/use-save-item-product'
import Options from './components/options'
import { CourseGateway } from './course-gateway'
import { digitalProductInitFields } from '../constants/mandatory-fields'

interface ECourseEditProps {
  storeId: StoreId
  item?: ProductECourseItemViewType
  languageCode: LanguageCodeEnum
}

const Edit: FC<ECourseEditProps> = ({ storeId, item, languageCode }) => {
  const { t } = useLocoTranslation()

  const { data: storeView } = useStore(storeId)

  const [tab, setTab] = useState<ECourseTabEnum>(ECourseTabEnum.thumbnail)
  const [showSavingButtons, setShowSavingButtons] = useState(true)
  const { setState } = useDesignContext()

  const defaultValuesWithLoco: ProductECourseItemEditType = {
    ...defaultValues,
    draft: false,
    title: t('dashboard.store.product.e_course.title_default', { lng: languageCode }),
    subTitle: t('dashboard.store.product.e_course.subtitle_default', { lng: languageCode }),
    buttonTitle: t('dashboard.store.product.e_course.button_default', { lng: languageCode }),
    checkoutPage: {
      ...defaultValues.checkoutPage,
      title: t('dashboard.store.product.e_course.checkout.title_default', { lng: languageCode }),
      description: t('dashboard.store.product.e_course.checkout.subtitle_default_tip-tap', {
        lng: languageCode,
      }),
      buttonTitle: t('dashboard.store.product.e_course.checkout.button_default', {
        lng: languageCode,
      }),
    },
    thankYouPage: {
      title: t('dashboard.store.product.download.thank_you.title_default', { lng: languageCode }),
      text: t('dashboard.store.product.download.thank_you.subtitle_default', { lng: languageCode }),
    },
  }

  const defaultProductValuesWithLoco: DigitalProductChangeInterface = {
    name: t('dashboard.store.product.download.title_default', { lng: languageCode }),
    resources: [],
    pricePlans: [],
    confirmationEmail: null,
  }

  const [tempState, setTempState] = useState<ProductECourseItemEditType>(
    item || defaultValuesWithLoco,
  )
  const [isDataSaved, setIsDataSaved] = useState<boolean>(true)

  const { data: digitalProduct } = useDigitalProduct(tempState.checkoutPage.digitalProductId)

  const [tempProduct, setTempProduct] = useState(digitalProduct ?? defaultProductValuesWithLoco)

  const resource = tempProduct.resources[0]
  const courseId =
    (resource && resource.type === DigitalResourcesTypeEnum.membership_course && resource.course) ||
    undefined
  useCoursePrecache(courseId)

  useEffect(() => {
    if (digitalProduct) {
      setTempProduct(digitalProduct)
    }
  }, [digitalProduct])

  const { errors: initialErrors, setErrors: setEditItemErrors } = useStoreEditItemContext()
  const [errors, setErrors] = useState<ProductECourseItemErrorsType>({
    ...defaultErrors,
    ...initialErrors,
  })

  const [productErrors, setProductErrors] = useState<DigitalProductErrorInterface>(
    defaultDigitalProductErrors,
  )

  useEffect(() => {
    return () => {
      setEditItemErrors({})
    }
  }, [])

  useStoreContactFields({
    savedFields: tempState.checkoutPage.fields,
    initFields: digitalProductInitFields,
    onChange: fields =>
      setTempState(prev => ({ ...prev, checkoutPage: { ...prev.checkoutPage, fields } })),
  })

  const { onPublish, isFetchingPublish, isFetchingDraft, onSaveDraft } = useSaveItemProduct({
    tempState,
    tempProduct,
    setTempProduct: setTempProduct as Dispatch<SetStateAction<DigitalProductChangeInterface>>,
    setTempState,
    storeId,
    setErrors,
    setProductErrors,
    defaultErrors,
  })

  const isPublishAllowed = [ECourseTabEnum.thank_you, ECourseTabEnum.options].includes(tab)

  const isCreatingProcess = !item

  const { getTabsWithErrors } = useErrorMessageInTabs()

  const errorsByTab = getTabsWithErrors({
    errors,
    digitalProductErrors: productErrors,
  })

  const isNew = !item

  const inReadyToSave = isNew ? true : !!digitalProduct

  const tabs: ItemTabType<ECourseTabEnum>[] = [
    {
      key: ECourseTabEnum.thumbnail,
      caption: t(tabLocoKeys[ECourseTabEnum.thumbnail]),
      hasError: errorsByTab[ECourseTabEnum.thumbnail],
    },
    {
      key: ECourseTabEnum.course,
      caption: t(tabLocoKeys[ECourseTabEnum.course]),
      hasError: errorsByTab[ECourseTabEnum.course],
    },
    {
      key: ECourseTabEnum.checkout,
      caption: t(tabLocoKeys[ECourseTabEnum.checkout]),
      hasError: errorsByTab[ECourseTabEnum.checkout],
    },
    {
      key: ECourseTabEnum.thank_you,
      caption: t(tabLocoKeys[ProductDownloadTabEnum.thank_you]),
      hasError: errorsByTab[ProductDownloadTabEnum.thank_you],
    },
    {
      key: ECourseTabEnum.options,
      caption: t(tabLocoKeys[ECourseTabEnum.options]),
      hasError: errorsByTab[ECourseTabEnum.options],
    },
  ]

  useEffect(() => {
    if (item || digitalProduct) {
      setIsDataSaved(
        deepCompare(
          { item: tempState, digitalProduct: tempProduct },
          {
            item: item ?? defaultValuesWithLoco,
            digitalProduct: digitalProduct ?? defaultProductValuesWithLoco,
          },
        ),
      )
    } else {
      setIsDataSaved(
        deepCompare(
          {
            item: tempState,
            digitalProduct: tempProduct,
          },
          {
            item: {
              ...defaultValuesWithLoco,
              checkoutPage: {
                ...defaultValuesWithLoco.checkoutPage,
                fields: tempState.checkoutPage.fields,
                description: tempState.checkoutPage.description,
              },
              style: tempState.style,
            },
            digitalProduct: defaultProductValuesWithLoco,
          },
        ),
      )
    }
  }, [tempState, tempProduct])

  useEffect(() => {
    setShowSavingButtons(tab !== ECourseTabEnum.course || true)
    tab !== ECourseTabEnum.options &&
      tab !== ECourseTabEnum.course &&
      setState(storeView?.design ?? defaultDesignValues.default)
  }, [tab])

  return (
    <div className={twMerge(STORE_ITEM_CLASS_NAME, 'flex flex-col gap-5')}>
      <ItemTabs tabs={tabs} currentTab={tab} setTab={setTab} />
      {tab === ECourseTabEnum.thumbnail && (
        <Thumbnail
          imageType={StoreImageEnum.e_course_thumbnail}
          tempState={tempState}
          setTempState={setTempState}
          errors={errors}
        />
      )}
      {tab === ECourseTabEnum.checkout && (
        <Checkout
          tempState={tempState}
          setTempState={setTempState}
          tempProduct={tempProduct}
          setTempProduct={setTempProduct as Dispatch<SetStateAction<DigitalProductChangeInterface>>}
          errors={errors}
          pricePlanErrors={productErrors.pricePlans}
          currency={storeView?.currency}
          paymentMethods={storeView?.paymentMethods}
          imageType={StoreImageEnum.e_course_cover}
        />
      )}
      {tab === ECourseTabEnum.course && (
        <CourseGateway
          courseId={tempState.courseId ?? undefined}
          tempProduct={tempProduct}
          setTempProduct={setTempProduct as Dispatch<SetStateAction<DigitalProductChangeInterface>>}
          tempState={tempState}
          setTempState={setTempState}
          errors={errors}
          onToggleLectureEditor={lectureId => {
            setShowSavingButtons(tab !== ECourseTabEnum.course || !lectureId)
            !!lectureId && setState(defaultDesignValues.default)
          }}
        />
      )}
      {tab === ECourseTabEnum.options && (
        <Options
          courseId={tempState.courseId ?? undefined}
          confirmationEmail={tempProduct.confirmationEmail}
          onChange={confirmationEmail => setTempProduct(prev => ({ ...prev, confirmationEmail }))}
          errors={productErrors.confirmationEmail}
        />
      )}
      {tab === ECourseTabEnum.thank_you && (
        <ThankYouTab
          thankYouPage={tempState.thankYouPage}
          onChange={thankYouPage => setTempState(prev => ({ ...prev, thankYouPage }))}
          errors={errors}
        />
      )}
      {showSavingButtons && (
        <ItemActions
          publish={{
            disabled: !inReadyToSave,
            onClick: isPublishAllowed || !isCreatingProcess ? onPublish : undefined,
            isFetching: isFetchingPublish,
          }}
          saveDraft={{
            disabled: !inReadyToSave,
            onClick: onSaveDraft,
            isFetching: isFetchingDraft,
          }}
          next={{
            onClick: isCreatingProcess && !isPublishAllowed ? () => setTab(getNextTab) : undefined,
          }}
        />
      )}
      <ConfirmationModalBeforeExit
        confirmationText={t('dashboard.store.confirmation_message_before_exit')}
        hideModal={isDataSaved || isFetchingPublish || isFetchingDraft}
      />
    </div>
  )
}

export default Edit
